import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const REF_STORAGE_KEY = "minzo_ref";

function getRefCode(): string | null {
  // Check URL params first
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  if (ref) {
    sessionStorage.setItem(REF_STORAGE_KEY, ref);
    // Clean URL without reload
    const url = new URL(window.location.href);
    url.searchParams.delete("ref");
    window.history.replaceState({}, "", url.toString());
    return ref;
  }
  return sessionStorage.getItem(REF_STORAGE_KEY);
}

async function logActivity(
  refCode: string,
  pageUrl: string,
  eventType: string,
  metadata: Record<string, unknown> = {}
) {
  try {
    await supabase.from("client_activity" as any).insert({
      ref_code: refCode,
      page_url: pageUrl,
      event_type: eventType,
      metadata,
    } as any);
  } catch {
    // Silent fail — don't disrupt UX
  }
}

export function useClientTracking() {
  const location = useLocation();
  const refCode = useRef<string | null>(null);
  const pageEntryTime = useRef<number>(Date.now());
  const maxScroll = useRef<number>(0);

  const syncRefCode = () => {
    const latestRef = getRefCode();
    if (latestRef) {
      refCode.current = latestRef;
    }
    return refCode.current;
  };

  // Keep ref code synced after redirects/query parsing
  useEffect(() => {
    syncRefCode();
  }, [location.pathname, location.search]);

  // Track page views and time spent
  useEffect(() => {
    const ref = syncRefCode();
    if (!ref) return;

    const path = location.pathname;
    pageEntryTime.current = Date.now();
    maxScroll.current = 0;

    // Log page view
    logActivity(ref, path, "page_view");

    // Track scroll depth
    const handleScroll = () => {
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollableHeight <= 0) return;

      const scrollPercent = Math.round((window.scrollY / scrollableHeight) * 100);
      if (scrollPercent > maxScroll.current) {
        maxScroll.current = scrollPercent;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });

    // Log time spent + scroll depth on leave
    return () => {
      window.removeEventListener("scroll", handleScroll);
      const timeSpent = Math.round((Date.now() - pageEntryTime.current) / 1000);
      if (timeSpent > 1) {
        logActivity(ref, path, "page_exit", {
          time_spent_seconds: timeSpent,
          max_scroll_percent: maxScroll.current,
        });
      }
    };
  }, [location.pathname, location.search]);

  // Track CTA clicks
  useEffect(() => {
    const ref = syncRefCode();
    if (!ref) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest("button, a[href]");
      if (!button) return;

      const text = button.textContent?.trim().slice(0, 100) || "";
      const href = (button as HTMLAnchorElement).href || "";

      // Only track meaningful CTA clicks
      if (
        button.tagName === "A" ||
        button.closest("[data-track-cta]") ||
        text.toLowerCase().includes("request") ||
        text.toLowerCase().includes("submit") ||
        text.toLowerCase().includes("contact") ||
        text.toLowerCase().includes("concierge")
      ) {
        logActivity(ref, location.pathname, "cta_click", {
          button_text: text,
          href,
        });
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [location.pathname, location.search]);
}
